import React, { useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Svg,
  Path,
  Circle,
  Polyline,
  Rect,
  Line,
} from "@react-pdf/renderer";
import { normalizePackRecommendation } from "./packCatalog";

// Registrar fuentes (opcional, puedes usar las por defecto)
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
      fontWeight: 500,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: 700,
    },
  ],
});

// Paleta base del PDF
const basePalette = {
  primary: "#E6007E",
  primaryLight: "#F2596A",
  primaryWash: "#faf9fe",
  success: "#0F9D58",
  successLight: "#4CC38A",
  successWash: "#f8fdfb",
  textPrimary: "#0f172a",
  textSecondary: "#475569",
  background: "#ffffff",
  backgroundLight: "#f8fafc",
  surface: "#ffffff",
  cardBorder: "#e2e8f0",
};

// Estilos dinámicos basados en la paleta
const createStyles = (palette) =>
  StyleSheet.create({
    page: {
      padding: 40,
      fontFamily: "Roboto",
      backgroundColor: palette.background,
    },
    header: {
      alignItems: "center",
      marginBottom: 30,
    },
    iconCircle: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: palette.primary,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 15,
    },
    headerTitle: {
      fontSize: 22,
      fontWeight: 700,
      color: palette.textPrimary,
      textAlign: "center",
      marginBottom: 8,
    },
    headerSubtitle: {
      fontSize: 11,
      color: palette.textSecondary,
      textAlign: "center",
      maxWidth: 420,
      lineHeight: 1.4,
    },
    cardPrimary: {
      backgroundColor: palette.primaryWash,
      borderRadius: 12,
      padding: 16,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: palette.cardBorder,
    },
    cardSuccess: {
      backgroundColor: palette.successWash,
      borderRadius: 12,
      padding: 16,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: palette.cardBorder,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 10,
      gap: 8,
    },
    cardTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: palette.primary,
    },
    cardTitleSuccess: {
      fontSize: 13,
      fontWeight: 600,
      color: palette.success,
    },
    cardBody: {
      fontSize: 11,
      color: palette.textPrimary,
      lineHeight: 1.6,
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 18,
      marginTop: 10,
      gap: 8,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: palette.textPrimary,
    },
    strategyGrid: {
      flexDirection: "row",
      marginBottom: 24,
    },
    strategyCard: {
      flexGrow: 1,
      flexBasis: 0,
      borderRadius: 12,
      padding: 14,
      backgroundColor: palette.surface,
      borderWidth: 1,
      borderColor: palette.cardBorder,
      minHeight: 120,
    },
    strategyCardSpacing: {
      marginRight: 12,
    },
    strategyNumber: {
      fontSize: 12,
      fontWeight: 700,
      color: palette.primary,
      marginBottom: 6,
    },
    strategyText: {
      fontSize: 10,
      color: palette.textPrimary,
      lineHeight: 1.5,
    },
    twoColumnGrid: {
      flexDirection: "row",
      marginBottom: 16,
    },
    halfCard: {
      flex: 1,
      flexBasis: 0,
    },
    columnSpacing: {
      marginRight: 12,
    },
    packName: {
      fontSize: 13,
      fontWeight: 700,
      color: palette.textPrimary,
      marginBottom: 6,
    },
    packPrice: {
      fontSize: 13,
      fontWeight: 500,
      color: palette.textSecondary,
      marginBottom: 10,
    },
    benefitItem: {
      flexDirection: "row",
      alignItems: "flex-start",
      marginBottom: 8,
    },
    checkMark: {
      width: 16,
      height: 16,
      marginRight: 8,
      marginTop: 1,
    },
    benefitText: {
      fontSize: 10,
      color: palette.textPrimary,
      lineHeight: 1.5,
      flex: 1,
    },
    footer: {
      marginTop: 32,
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: palette.cardBorder,
      alignItems: "center",
    },
    footerText: {
      fontSize: 10,
      color: palette.textSecondary,
      textAlign: "center",
    },
  });

// Componente de Icono SVG
const PdfIcon = ({ name, size = 20, color = "#000000" }) => {
  // Common props for outlines
  const props = {
    stroke: color,
    fill: "none",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "Sparkles":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path
            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
            {...props}
          />
          {/* Cruz arriba-derecha */}
          <Path d="M19 3v4" {...props} />
          <Path d="M17 5h4" {...props} />
          {/* Cruz abajo-izquierda */}
          <Path d="M5 17v4" {...props} />
          <Path d="M3 19h4" {...props} />
        </Svg>
      );
    case "ClipboardList":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Rect width="8" height="4" x="8" y="2" rx="1" ry="1" {...props} />
          <Path
            d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
            {...props}
          />
          <Path d="M12 11h4" {...props} />
          <Path d="M12 16h4" {...props} />
          <Path d="M8 11h.01" {...props} />
          <Path d="M8 16h.01" {...props} />
        </Svg>
      );
    case "Target":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Circle cx="12" cy="12" r="10" {...props} />
          <Circle cx="12" cy="12" r="6" {...props} />
          <Circle cx="12" cy="12" r="2" {...props} />
        </Svg>
      );
    case "Package":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path d="m7.5 4.27 9 5.15" {...props} />
          <Path
            d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
            {...props}
          />
          <Path d="m3.3 7 8.7 5 8.7-5" {...props} />
          <Path d="M12 22v-9.18" {...props} />
        </Svg>
      );
    case "TrendingUp":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Polyline points="22 7 13.5 15.5 8.5 10.5 2 17" {...props} />
          <Polyline points="16 7 22 7 22 13" {...props} />
        </Svg>
      );
    case "Check":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Polyline points="20 6 9 17 4 12" {...props} />
        </Svg>
      );
    case "CheckCircle":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Circle cx="12" cy="12" r="10" {...props} />
          <Path d="m9 12 2 2 4-4" {...props} />
        </Svg>
      );
    default:
      return null;
  }
};

// Componente de tarjeta de estrategia      
const StrategyCardPDF = ({ number, title, description, styles, isLast }) => (
  <View
    style={[styles.strategyCard, !isLast ? styles.strategyCardSpacing : null]}
  >
    <Text style={styles.strategyNumber}>
      {number}. {title}
    </Text>
    <Text style={styles.strategyText}>{description}</Text>
  </View>
);

// Componente principal del documento PDF
const LeadmagnetPDF = ({ strategyData, palette }) => {
  if (!strategyData) return null;

  const colors = useMemo(() => {
    if (!palette) return basePalette;
    return {
      ...basePalette,
      ...palette,
      primaryWash:
        palette?.primaryWash ||
        palette?.primaryLight ||
        basePalette.primaryWash,
      successWash:
        palette?.successWash ||
        palette?.successLight ||
        basePalette.successWash,
      backgroundLight:
        palette?.backgroundLight ||
        palette?.surface ||
        basePalette.backgroundLight,
      surface:
        palette?.surface || palette?.backgroundLight || basePalette.surface,
      cardBorder: palette?.cardBorder || basePalette.cardBorder,
    };
  }, [palette]);

  const styles = useMemo(() => createStyles(colors), [colors]);

  const strategySections = [
    strategyData?.estrategia?.atraccion && {
      number: "1",
      title: "Atracción",
      description: strategyData.estrategia.atraccion,
    },
    strategyData?.estrategia?.captacion && {
      number: "2",
      title: "Captación",
      description: strategyData.estrategia.captacion,
    },
    strategyData?.estrategia?.conversion && {
      number: "3",
      title: "Conversión",
      description: strategyData.estrategia.conversion,
    },
  ].filter(Boolean);

  const hasPack = Boolean(strategyData?.pack_recomendado);
  const hasBenefits = Boolean(strategyData?.beneficios?.length);
  const packData = normalizePackRecommendation(strategyData?.pack_recomendado);
  const packName =
    typeof packData === "object" && packData?.nombre
      ? packData.nombre
      : "Pack Recomendado";
  const packPrice =
    typeof packData === "object" && packData?.precio ? packData.precio : null;
  const packDescription =
    (typeof packData === "object" &&
      (packData.descripcion || packData.justificacion)) ||
    (typeof packData === "string" ? packData : "");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.iconCircle}>
            <PdfIcon name="Sparkles" size={32} color="#ffffff" />
          </View>
          <Text style={styles.headerTitle}>
            ¡Tu Plan de Marketing Personalizado está Listo!
          </Text>
          <Text style={styles.headerSubtitle}>
            Basado en tus respuestas, hemos diseñado una estrategia para
            impulsar tu negocio.
          </Text>
        </View>

        {strategyData?.diagnostico && (
          <View wrap={false} style={styles.cardPrimary}>
            <View style={styles.cardHeader}>
              <PdfIcon name="ClipboardList" size={16} color={colors.primary} />
              <Text style={styles.cardTitle}>Diagnóstico Actual</Text>
            </View>
            <Text style={styles.cardBody}>{strategyData.diagnostico}</Text>
          </View>
        )}

        {strategySections.length > 0 && (
          <View wrap={false}>
            <View style={styles.sectionHeader}>
              <PdfIcon name="Target" size={18} color={colors.textPrimary} />
              <Text style={styles.sectionTitle}>
                Estrategia Recomendada: Método SERSEO
              </Text>
            </View>

            <View style={styles.strategyGrid}>
              {strategySections.map((section, index) => (
                <StrategyCardPDF
                  key={section.number}
                  {...section}
                  styles={styles}
                  isLast={index === strategySections.length - 1}
                />
              ))}
            </View>
          </View>
        )}

        {(hasPack || hasBenefits) && (
          <View wrap={false} style={styles.twoColumnGrid}>
            {hasPack && (
              <View
                wrap={false}
                style={[
                  styles.cardPrimary,
                  styles.halfCard,
                  hasBenefits ? styles.columnSpacing : null,
                ]}
              >
                <View style={styles.cardHeader}>
                  <PdfIcon name="Package" size={16} color={colors.primary} />
                  <Text style={styles.cardTitle}>Pack SERSEO Recomendado</Text>
                </View>
                <Text style={styles.packName}>{packName}</Text>
                {packPrice && <Text style={styles.packPrice}>{packPrice}</Text>}
                {!!packDescription && (
                  <Text style={styles.cardBody}>{packDescription}</Text>
                )}
              </View>
            )}

            {hasBenefits && (
              <View wrap={false} style={[styles.cardSuccess, styles.halfCard]}>
                <View style={styles.cardHeader}>
                  <PdfIcon name="TrendingUp" size={16} color={colors.success} />
                  <Text style={styles.cardTitleSuccess}>
                    Beneficios Esperados
                  </Text>
                </View>
                {strategyData.beneficios.map((beneficio, index) => (
                  <View
                    key={`${beneficio}-${index}`}
                    style={styles.benefitItem}
                  >
                    <View style={styles.checkMark}>
                      <PdfIcon
                        name="CheckCircle"
                        size={16}
                        color={colors.success}
                      />
                    </View>
                    <Text style={styles.benefitText}>{beneficio}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default LeadmagnetPDF;
